/**
 * Customer types — company-scoped classification of customer records.
 *
 * A Settings tab rather than a rail item: types are a small, stable vocabulary an
 * administrator sets up once (and the Ethos sync targets), not a working list
 * anyone visits daily.
 *
 * What the contract fixes, and what this screen therefore refuses to do:
 *
 * - **The key is immutable.** `UpdateCustomerTypeRequest` accepts name,
 *   description and status — never `key`. So the key is a create-time decision,
 *   rendered as read-only mono text afterwards, and the create form says so
 *   before it is committed rather than after.
 * - **Creates carry a caller-generated UUID**, so a retried create replays
 *   instead of making a second type. The id is generated ONCE per attempt and
 *   reused until that attempt succeeds.
 * - **Every update sends `expected_version`** from the last response. A 409
 *   (`CUSTOMER_TYPE_CONFLICT`) means someone else changed the row; the edit is
 *   kept, the row is re-read, and the operator decides.
 * - **Archive, never delete.** There is no delete operation, because existing
 *   customer assignments point at these ids. Archived types stay resolvable and
 *   are excluded only from NEW assignments.
 *
 * Scope note: `customer_types.read` / `.write` are `session-company` scoped and
 * this app has no company switcher, so the list is re-read on tenant identity
 * change — the narrowest scope the session shell actually exposes.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Btn, Card, Icon, Input, Pill, SegmentedControl, T, tx } from "@v0y4ge/jrni-ui";
import { ApiError, createCustomerType, listCustomerTypes, updateCustomerType } from "@v0y4ge/api-client";
import type { CustomerTypeDto } from "@v0y4ge/api-contracts";
import { PageHeader } from "../../shell/chrome";
import SettingsShell from "./SettingsShell";
import { toast } from "../../shell/toast";
import { can, getConfirmedTenantSlug, getTenantGeneration, onTenantChange, onTenantSwitching, useTenant } from "../../shell/access";
import { useT } from "../../lib/i18n";

const KEY_RE = /^[a-z][a-z0-9_]{0,63}$/;
const NAME_MAX = 120;
const DESC_MAX = 2000;

const cardHead: CSSProperties = { padding: "14px 16px", borderBottom: `1px solid ${T.border}` };
const fieldLabel: CSSProperties = { fontSize: 12, fontWeight: 600, color: T.n2, display: "block", marginBottom: 4 };
const helpText: CSSProperties = { fontSize: 11, color: T.soft, lineHeight: 1.5, margin: "4px 0 0" };
const mono: CSSProperties = { fontFamily: T.fontMono, fontSize: 11, color: T.soft };

const codeOf = (e: unknown): string => (e instanceof ApiError ? e.code : "");
const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** The key a name suggests. Truncated to the pattern's own 64-character bound. */
export function keyFromName(name: string): string {
  const slug = name.trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_{2,}/g, "_")
    .slice(0, 64);
  return /^[a-z]/.test(slug) ? slug : slug ? `t_${slug}`.slice(0, 64) : "";
}

type StatusFilter = "active" | "archived" | "all";

export default function SettingsCustomerTypes() {
  const t = useT();
  const tenant = useTenant();
  const scopeKey = tenant?.slug ?? "";
  const canWrite = can("customer_types.write");

  const [items, setItems] = useState<CustomerTypeDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("active");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /* A read that started under a previous filter or tenant must not land. */
  const generation = useRef(0);

  const PAGE_SIZE = 200;
  const queryOf = (query: { search: string; status: StatusFilter }, p: number) => ({
    page: p,
    page_size: PAGE_SIZE,
    ...(query.status === "all" ? {} : { status: query.status }),
    ...(query.search.trim() ? { search: query.search.trim() } : {}),
  });

  /* First page of the SERVER-side filtered list. The table is paginated on the
     server (search + status + page); further pages append via `loadMore`, and
     the footer states how many of the advertised total are shown. */
  const load = useCallback(async (query: { search: string; status: StatusFilter }, quiet: boolean) => {
    const gen = ++generation.current;
    setLoadingMore(false);
    if (!quiet) setLoading(true);
    try {
      const res = await listCustomerTypes(queryOf(query, 1));
      if (gen !== generation.current) return;
      setItems(res.items);
      setTotal(res.total);
      setPage(1);
      setLoadError(null);
    } catch (e) {
      if (gen !== generation.current) return;
      setLoadError(messageOf(e));
    } finally { if (gen === generation.current && !quiet) setLoading(false); }
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    const gen = generation.current;
    setLoadingMore(true);
    try {
      const res = await listCustomerTypes(queryOf({ search, status }, page + 1));
      if (gen !== generation.current) return;
      // De-duplicate: a row created or archived between page reads can shift
      // the boundary and appear twice.
      setItems((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        return [...prev, ...res.items.filter((x) => !seen.has(x.id))];
      });
      setTotal(res.total);
      setPage(page + 1);
    } catch (e) {
      if (gen !== generation.current) return;
      toast({ tone: "warn", title: t("settings.customerTypes.moreFailed", "Couldn\u2019t read the next page"), subtitle: messageOf(e) });
    } finally { if (gen === generation.current) setLoadingMore(false); }
  }, [loadingMore, page, search, status, t]);

  /* Tenant switch: purge instantly, re-read once the new session is confirmed. */
  useEffect(() => {
    const purge = () => { generation.current += 1; setItems([]); setTotal(0); setPage(1); setEditingId(null); setCreating(false); setBusyId(null); setLoadingMore(false); setLoadError(null); setLoading(true); };
    const off1 = onTenantSwitching(purge);
    const off2 = onTenantChange(() => { void load({ search, status }, false); });
    return () => { off1(); off2(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status]);

  /* Search is a server-side filter, so it is debounced — one read per pause,
     not one per keystroke. */
  useEffect(() => {
    const handle = window.setTimeout(() => { void load({ search, status }, false); }, search ? 300 : 0);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, scopeKey]);

  /** Replace one row in place after an update, without refetching the list. */
  const merge = useCallback((next: CustomerTypeDto) => {
    setItems((prev) => {
      const hit = prev.some((x) => x.id === next.id);
      const mapped = hit ? prev.map((x) => (x.id === next.id ? next : x)) : [next, ...prev];
      // A row that no longer matches the active filter leaves the list, so
      // archiving under "Active" visibly removes it instead of lying.
      return status === "all" ? mapped : mapped.filter((x) => x.status === status);
    });
  }, [status]);

  const applyUpdate = useCallback(async (
    row: CustomerTypeDto,
    patch: { name?: string; description?: string | null; status?: "active" | "archived" },
    successTitle: string,
  ): Promise<boolean> => {
    const scope = getConfirmedTenantSlug();
    const tenantGeneration = getTenantGeneration();
    const isCurrent = () => scope !== null && scope === getConfirmedTenantSlug() && tenantGeneration === getTenantGeneration();
    if (!isCurrent()) return false;
    setBusyId(row.id);
    try {
      const next = await updateCustomerType(row.id, { expected_version: row.version, ...patch });
      if (!isCurrent()) return false;
      merge(next);
      toast({ tone: "success", title: successTitle });
      // The merged row is the server's response; the quiet re-read confirms
      // the persisted list (ordering, filter membership) without a flicker.
      void load({ search, status }, true);
      return true;
    } catch (e) {
      if (!isCurrent()) return false;
      const code = codeOf(e);
      if (code === "CUSTOMER_TYPE_CONFLICT") {
        // Re-read so the operator sees the current row before deciding, and
        // keep their edit open rather than discarding it.
        await load({ search, status }, true);
        if (!isCurrent()) return false;
        toast({
          tone: "warn",
          title: t("settings.customerTypes.conflictTitle", "This type changed while you were editing"),
          subtitle: t("settings.customerTypes.conflictSub", "The current version is shown. Nothing was overwritten \u2014 reapply your change if it still applies."),
        });
      } else if (code === "CUSTOMER_TYPE_NOT_FOUND") {
        await load({ search, status }, true);
        if (!isCurrent()) return false;
        toast({ tone: "warn", title: t("settings.customerTypes.goneTitle", "This type no longer exists") });
      } else if (code === "CUSTOMER_TYPE_INVALID") {
        toast({ tone: "warn", title: t("settings.customerTypes.updateInvalid", "The change was rejected"), subtitle: t("settings.customerTypes.invalidSub", "Name must be 1\u2013120 characters; description at most 2000.") });
      } else if (e instanceof ApiError && (e.status === 403 || e.status === 401)) {
        toast({ tone: "warn", title: t("settings.customerTypes.forbiddenUpdate", "You don\u2019t have permission to change customer types") });
      } else {
        toast({ tone: "warn", title: t("settings.customerTypes.updateFailed", "Couldn\u2019t save the change"), subtitle: messageOf(e) });
      }
      return false;
    } finally { if (isCurrent()) setBusyId(null); }
  }, [merge, load, search, status, t]);

  const counts = useMemo(() => ({
    active: items.filter((x) => x.status === "active").length,
    archived: items.filter((x) => x.status === "archived").length,
  }), [items]);

  return (
    <SettingsShell activeTab="customerTypes">
      <PageHeader
        title={t("settings.customerTypes.title", "Customer types")}
        subtitle={t("settings.customerTypes.subtitle", "How this company classifies its customer records. Types are referenced by assignments and by the SIS sync, so they are archived rather than deleted.")}
        actions={canWrite ? (
          <Btn icon={<Icon.plus size={14} />} disabled={creating} onClick={() => setCreating(true)}>
            {t("settings.customerTypes.new", "New type")}
          </Btn>
        ) : null}
      />

      {creating ? (
        <CreateTypePanel
          onCancel={() => setCreating(false)}
          onCreated={(row) => {
            setCreating(false);
            // A new type is always active; surface it even if the filter is on
            // "Archived", by moving the filter to where the row actually is.
            if (status === "archived") setStatus("active"); else { merge(row); void load({ search, status }, true); }
          }}
        />
      ) : null}

      <Card style={{ padding: 0 }}>
        <div style={{ ...cardHead, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 220px", minWidth: 180 }}>
            <Input
              value={search}
              placeholder={t("settings.customerTypes.searchPlaceholder", "Search types\u2026")}
              iconLeft={<Icon.search size={14} />}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            />
          </div>
          <SegmentedControl<StatusFilter>
            size="sm"
            ariaLabel={t("settings.customerTypes.filterLabel", "Status filter")}
            value={status}
            onChange={setStatus}
            options={[
              { value: "active", label: t("settings.customerTypes.filterActive", "Active") },
              { value: "archived", label: t("settings.customerTypes.filterArchived", "Archived") },
              { value: "all", label: t("settings.customerTypes.filterAll", "All") },
            ]}
          />
        </div>

        {loading ? (
          <div style={{ padding: 20, fontSize: 12, color: T.soft }}>{t("settings.customerTypes.loading", "Loading customer types\u2026")}</div>
        ) : loadError ? (
          <div style={{ padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.red1 }}>{t("settings.customerTypes.loadFailed", "The type list couldn\u2019t be read")}</div>
            <div style={{ fontSize: 12, color: T.n1, marginTop: 4 }}>{loadError}</div>
            <div style={{ marginTop: 10 }}>
              <Btn variant="secondary" size="sm" icon={<Icon.refresh size={12} />} onClick={() => { void load({ search, status }, false); }}>
                {t("common.actions.retry", "Retry")}
              </Btn>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: 24, fontSize: 12, color: T.soft, lineHeight: 1.6 }}>
            {search.trim()
              ? t("settings.customerTypes.emptySearch", "No type matches that search.")
              : status === "archived"
                ? t("settings.customerTypes.emptyArchived", "No type has been archived.")
                : t("settings.customerTypes.empty", "No customer types yet. Create one before pointing the SIS sync at it.")}
          </div>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {items.map((row, i) => (
              <li key={row.id} style={{ borderTop: i ? `1px solid ${T.border}` : "none" }}>
                {editingId === row.id ? (
                  <EditTypeRow
                    row={row}
                    busy={busyId === row.id}
                    onCancel={() => setEditingId(null)}
                    onSave={async (patch) => {
                      const ok = await applyUpdate(row, patch, t("settings.customerTypes.saved", "Customer type saved"));
                      if (ok) setEditingId(null);
                    }}
                  />
                ) : (
                  <div style={{ padding: "12px 16px", display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{row.name}</span>
                        <code style={mono}>{row.key}</code>
                        {row.status === "archived" ? <Pill tone="neutral">{t("settings.customerTypes.archived", "Archived")}</Pill> : null}
                      </div>
                      {row.description ? (
                        <div style={{ fontSize: 12, color: T.n2, marginTop: 3, lineHeight: 1.55, textWrap: "pretty" }}>{row.description}</div>
                      ) : null}
                    </div>
                    {canWrite ? (
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        <Btn variant="ghost" size="sm" disabled={busyId != null} onClick={() => setEditingId(row.id)}>
                          {t("common.actions.edit", "Edit")}
                        </Btn>
                        <Btn
                          variant="ghost"
                          size="sm"
                          disabled={busyId != null}
                          onClick={() => {
                            void applyUpdate(
                              row,
                              { status: row.status === "active" ? "archived" : "active" },
                              row.status === "active"
                                ? t("settings.customerTypes.archivedToast", "Type archived")
                                : t("settings.customerTypes.reactivatedToast", "Type reactivated"),
                            );
                          }}
                        >
                          {row.status === "active"
                            ? t("settings.customerTypes.archive", "Archive")
                            : t("settings.customerTypes.reactivate", "Reactivate")}
                        </Btn>
                      </div>
                    ) : null}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {!loading && !loadError && items.length > 0 ? (
          <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.border}`, fontSize: 11, color: T.soft, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <span>
              {items.length < total
                ? `${items.length} ${t("settings.customerTypes.ofTotal", "of")} ${total} ${t("settings.customerTypes.shown", "shown")}`
                : status === "all"
                  ? `${counts.active} ${t("settings.customerTypes.countActive", "active")} \u00b7 ${counts.archived} ${t("settings.customerTypes.countArchived", "archived")}`
                  : `${items.length} ${status === "active" ? t("settings.customerTypes.countActive", "active") : t("settings.customerTypes.countArchived", "archived")}`}
              {canWrite ? null : ` \u00b7 ${t("settings.customerTypes.readOnly", "read-only")}`}
            </span>
            {items.length < total ? (
              <Btn variant="secondary" size="sm" disabled={loadingMore} onClick={() => { void loadMore(); }}>
                {loadingMore ? t("settings.customerTypes.loadingMore", "Loading\u2026") : t("settings.customerTypes.loadMore", "Load more")}
              </Btn>
            ) : null}
          </div>
        ) : null}
      </Card>
    </SettingsShell>
  );
}

/* ── Create ───────────────────────────────────────────────────────────────── */

function CreateTypePanel({ onCancel, onCreated }: { onCancel: () => void; onCreated: (row: CustomerTypeDto) => void }) {
  const t = useT();
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Stable across retries of THIS attempt, so a retried create replays the same
     id instead of producing a duplicate type. */
  const attemptId = useRef<string>(crypto.randomUUID());

  const effectiveKey = keyTouched ? key : keyFromName(name);
  const nameError = name.trim().length === 0
    ? null
    : name.trim().length > NAME_MAX ? t("settings.customerTypes.nameTooLong", "120 characters maximum.") : null;
  const keyError = effectiveKey.length === 0
    ? null
    : KEY_RE.test(effectiveKey) ? null : t("settings.customerTypes.keyInvalid", "Lowercase letters, digits and underscores, starting with a letter.");
  const ready = name.trim().length > 0 && effectiveKey.length > 0 && !nameError && !keyError;

  const submit = async () => {
    if (!ready || busy) return;
    const scope = getConfirmedTenantSlug();
    const tenantGeneration = getTenantGeneration();
    const isCurrent = () => scope !== null && scope === getConfirmedTenantSlug() && tenantGeneration === getTenantGeneration();
    if (!isCurrent()) return;
    setBusy(true); setError(null);
    try {
      const row = await createCustomerType({
        id: attemptId.current,
        key: effectiveKey,
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
      });
      if (!isCurrent()) return;
      toast({ tone: "success", title: t("settings.customerTypes.createdToast", "Customer type created") });
      onCreated(row);
    } catch (e) {
      if (!isCurrent()) return;
      const code = codeOf(e);
      setError(code === "CUSTOMER_TYPE_CONFLICT"
        ? t("settings.customerTypes.keyTaken", "A type with this key already exists in this company.")
        : code === "CUSTOMER_TYPE_INVALID"
          ? t("settings.customerTypes.invalid", "The server rejected this type. Check the key (lowercase letters, digits, underscores) and the name (1\u2013120 characters).")
          : e instanceof ApiError && (e.status === 403 || e.status === 401)
            ? t("settings.customerTypes.forbidden", "You don\u2019t have permission to create customer types.")
            : messageOf(e));
    } finally { if (isCurrent()) setBusy(false); }
  };

  return (
    <Card style={{ padding: 0, marginBottom: 16 }}>
      <div style={cardHead}>
        <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.customerTypes.createTitle", "New customer type")}</h3>
        <p style={{ ...tx.help, marginTop: 2 }}>{t("settings.customerTypes.createHelp", "The key identifies this type to integrations and cannot be changed later. The name can.")}</p>
      </div>
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          <div>
            <label style={fieldLabel}>{t("settings.customerTypes.nameLabel", "Name")}</label>
            <Input value={name} placeholder={t("settings.customerTypes.namePlaceholder", "e.g. Undergraduate student")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
            {nameError ? <p style={{ ...helpText, color: T.red1 }}>{nameError}</p> : null}
          </div>
          <div>
            <label style={fieldLabel}>{t("settings.customerTypes.keyLabel", "Key (permanent)")}</label>
            <Input value={effectiveKey} placeholder="undergraduate_student"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setKeyTouched(true); setKey(e.target.value); }} />
            {keyError
              ? <p style={{ ...helpText, color: T.red1 }}>{keyError}</p>
              : <p style={helpText}>{t("settings.customerTypes.keyHelp", "Suggested from the name until you edit it. Immutable after creation.")}</p>}
          </div>
        </div>
        <div>
          <label style={fieldLabel}>{t("settings.customerTypes.descLabel", "Description (optional)")}</label>
          {/* ui-raw: a multi-line description; jrni-ui has no Textarea primitive yet. */}
          <textarea
            value={description}
            maxLength={DESC_MAX}
            rows={2}
            onChange={(e) => setDescription(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.borderStrong}`, background: T.white, fontFamily: T.font, fontSize: 13, color: T.n1, lineHeight: 1.5, resize: "vertical", boxSizing: "border-box" }}
          />
        </div>
        {error ? <div style={{ fontSize: 12, color: T.red1, lineHeight: 1.5 }}>{error}</div> : null}
      </div>
      <div style={{ padding: "12px 16px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 8 }}>
        <Btn disabled={!ready || busy} onClick={() => { void submit(); }}>
          {busy ? t("settings.customerTypes.creating", "Creating\u2026") : t("settings.customerTypes.create", "Create type")}
        </Btn>
        <Btn variant="ghost" disabled={busy} onClick={onCancel}>{t("common.actions.cancel", "Cancel")}</Btn>
      </div>
    </Card>
  );
}

/* ── Edit (name + description; key and status are not editable here) ─────── */

function EditTypeRow({ row, busy, onCancel, onSave }: {
  row: CustomerTypeDto;
  busy: boolean;
  onCancel: () => void;
  onSave: (patch: { name?: string; description?: string | null }) => void | Promise<void>;
}) {
  const t = useT();
  const [name, setName] = useState(row.name);
  const [description, setDescription] = useState(row.description ?? "");

  const trimmedName = name.trim();
  const trimmedDesc = description.trim();
  const nameError = trimmedName.length === 0
    ? t("settings.customerTypes.nameRequired", "A name is required.")
    : trimmedName.length > NAME_MAX ? t("settings.customerTypes.nameTooLong", "120 characters maximum.") : null;

  const nameChanged = trimmedName !== row.name;
  const descChanged = trimmedDesc !== (row.description ?? "");
  // `UpdateCustomerTypeRequest` refuses an empty patch, so Save stays disabled
  // until something actually differs.
  const dirty = nameChanged || descChanged;

  return (
    <div style={{ padding: "14px 16px", background: T.n6, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
        <div>
          <label style={fieldLabel}>{t("settings.customerTypes.nameLabel", "Name")}</label>
          <Input value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
          {nameError ? <p style={{ ...helpText, color: T.red1 }}>{nameError}</p> : null}
        </div>
        <div>
          <label style={fieldLabel}>{t("settings.customerTypes.keyLabel", "Key (permanent)")}</label>
          <div style={{ height: 36, display: "flex", alignItems: "center" }}><code style={mono}>{row.key}</code></div>
          <p style={helpText}>{t("settings.customerTypes.keyLocked", "Set at creation and referenced by integrations \u2014 it cannot be changed.")}</p>
        </div>
      </div>
      <div>
        <label style={fieldLabel}>{t("settings.customerTypes.descLabel", "Description (optional)")}</label>
        {/* ui-raw: see CreateTypePanel — no Textarea primitive in jrni-ui yet. */}
        <textarea
          value={description}
          maxLength={DESC_MAX}
          rows={2}
          onChange={(e) => setDescription(e.target.value)}
          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.borderStrong}`, background: T.white, fontFamily: T.font, fontSize: 13, color: T.n1, lineHeight: 1.5, resize: "vertical", boxSizing: "border-box" }}
        />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Btn
          size="sm"
          disabled={busy || !dirty || nameError != null}
          onClick={() => {
            void onSave({
              ...(nameChanged ? { name: trimmedName } : {}),
              ...(descChanged ? { description: trimmedDesc.length ? trimmedDesc : null } : {}),
            });
          }}
        >
          {busy ? t("common.actions.saving", "Saving\u2026") : t("common.actions.save", "Save")}
        </Btn>
        <Btn variant="ghost" size="sm" disabled={busy} onClick={onCancel}>{t("common.actions.cancel", "Cancel")}</Btn>
      </div>
    </div>
  );
}
